import { Box, Container } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { KC_COLORS } from '../theme';
import { SearchResultRow } from './SearchResultRow';

const TABS = [
  { id: 'all', label: 'All Results' },
  { id: 'articles', label: 'Articles' },
  { id: 'categories', label: 'Categories' },
  { id: 'dictionary', label: 'Dictionary' },
];

export function SearchResultsPanel({ results, hasQuery }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab = TABS.some((tab) => tab.id === tabFromUrl)
    ? tabFromUrl
    : 'all';
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    const nextTab = TABS.some((tab) => tab.id === tabFromUrl)
      ? tabFromUrl
      : 'all';
    setActiveTab(nextTab);
  }, [results.query, tabFromUrl]);

  const counts = useMemo(
    () => ({
      all: results.all.length,
      articles: results.articles.length,
      categories: results.categories.length,
      dictionary: (results.dictionary || []).length,
    }),
    [results]
  );

  const visibleResults = useMemo(() => {
    if (activeTab === 'articles') return results.articles;
    if (activeTab === 'categories') return results.categories;
    if (activeTab === 'dictionary') return results.dictionary || [];
    return results.all;
  }, [activeTab, results]);

  const displayQuery = results.query;

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const next = new URLSearchParams(searchParams);
    if (tabId === 'all') {
      next.delete('tab');
    } else {
      next.set('tab', tabId);
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <Box className="kc-search-results w-full bg-white py-10 md:py-14">
      <Container maxWidth="lg">
        <div className="kc-search-summary flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6 mb-7 md:mb-8">
          <p className="kc-type-h3 m-0" style={{ color: KC_COLORS.primaryNavy }}>
            {hasQuery ? (
              <>
                Showing <span className="font-semibold">{counts.all}</span> results for{' '}
                <span className="font-semibold">&apos;{displayQuery}&apos;</span>
              </>
            ) : (
              <>
                Browse{' '}
                <span className="font-semibold">{counts.categories}</span> categories,{' '}
                <span className="font-semibold">{counts.dictionary}</span> dictionary terms, and{' '}
                <span className="font-semibold">{counts.articles}</span> articles
              </>
            )}
          </p>

          <Link
            to="/knowledge-center#kc-categories"
            className="kc-search-browse-categories inline-flex shrink-0 items-center justify-center self-start rounded-full px-5 py-2.5 kc-type-body font-semibold no-underline transition-opacity hover:opacity-90"
            style={{ backgroundColor: KC_COLORS.teal, color: '#FFFFFF' }}
          >
            Browse Categories
          </Link>
        </div>

        <div
          className="kc-search-tabs flex flex-wrap items-end gap-x-6 md:gap-x-8 border-b mb-6 md:mb-8"
          style={{ borderColor: KC_COLORS.border }}
        >
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            const count = counts[tab.id];
            const label =
              tab.id === 'all' ? tab.label : `${tab.label} (${count})`;

            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className="relative pb-3.5 kc-type-body font-semibold transition-colors"
                style={{
                  color: isActive ? KC_COLORS.teal : KC_COLORS.gray600,
                }}
              >
                {label}
                {isActive ? (
                  <span
                    className="absolute left-0 right-0 -bottom-px h-[2px] rounded-full"
                    style={{ backgroundColor: KC_COLORS.teal }}
                  />
                ) : null}
              </button>
            );
          })}
        </div>

        {visibleResults.length === 0 ? (
          <p className="kc-search-empty py-12 kc-type-body" style={{ color: KC_COLORS.gray600 }}>
            No results found.
          </p>
        ) : (
          <div
            className="kc-search-list rounded-2xl border bg-white overflow-hidden shadow-[0_6px_22px_rgba(10,29,58,0.06)]"
            style={{ borderColor: KC_COLORS.border }}
          >
            {visibleResults.map((result) => (
              <SearchResultRow key={result.id} result={result} />
            ))}
          </div>
        )}
      </Container>
    </Box>
  );
}
